import React, { useState, useEffect } from 'react';
import { Form } from '../../../ui/universe';
import { TagInput, Tooltip, Checkbox, Input, Textarea } from 'tdesign-react';
import { splitByCommas } from '../../../../utils/index.js';
import FieldSchemaEditor, { buildSchemaItemsForRequest, createEmptyField } from './FieldSchemaEditor';
import '../../../../style/RecognizeAgent.css';

/**
 * RecognizeAgent 任务类型选择 + 动态参数渲染器
 * 包含：任务类型卡片选择 + 根据类型动态显示的输入区域（Query、坐标等）
 */
function RecognizeAgentTaskTypeRenderer({ params, value, onChange, allParamsValue, onParamsChange }) {
  const [inputValue, setInputValue] = useState('');
  const [tags, setTags] = useState([]);

  const queryType = value;
  const queryValue = allParamsValue?.Query || [];
  const enableCoord = allParamsValue?.EnableCoord || false;

  // 切换任务类型时重置输入
  useEffect(() => {
    setInputValue('');
    setTags([]);
    // 清空 Query 和 EnableCoord
    onParamsChange && onParamsChange({ Query: [], EnableCoord: false });
  }, [queryType]);

  const handleQueryChange = (newQuery) => {
    onParamsChange && onParamsChange({ Query: newQuery });
  };

  const handleCoordChange = (val) => {
    onParamsChange && onParamsChange({ EnableCoord: val });
  };

  // 渲染某个任务类型对应的动态参数（紧挨在卡片下方）
  const renderInlineParams = (optionValue) => {
    if (value !== optionValue) return null;

    if (optionValue === 1) {
      const judgeValue = tags[0] || '';
      return (
        <div className="recognize-agent-inline-params">
          <div className="recognize-agent-dynamic-field__label">
            <span>判断内容</span>
            <Tooltip content="判断内容数量仅为1" theme="dark">
              <span className="ocrDemo-icon-question"></span>
            </Tooltip>
          </div>
          <Textarea
            placeholder="请输入判断内容，如：增值税专用发票"
            value={judgeValue}
            maxlength={100}
            autosize={{ minRows: 4, maxRows: 8 }}
            onChange={(val) => {
              // 智能判断：单条 Query 限制最大 100 字符，超出自动截断
              const safeVal = typeof val === 'string' ? val.slice(0, 100) : val;
              const newTags = safeVal ? [safeVal] : [];
              setTags(newTags);
              handleQueryChange(newTags);
            }}
          />
        </div>
      );
    }

    if (optionValue === 2) {
      return (
        <div className="recognize-agent-inline-params">
          <div className="recognize-agent-dynamic-field__label">
            <span>分类项（最多5个）</span>
            <Tooltip content="分类项数量必须在2-5个之间" theme="dark">
              <span className="ocrDemo-icon-question"></span>
            </Tooltip>
          </div>
          <TagInput
            value={tags}
            placeholder="请输入分类项，如：营业执照，回车确认"
            excessTagsDisplayType="break-line"
            size="medium"
            valueDisplay={({ value: displayTags, onClose }) =>
              displayTags?.map((item, index) => (
                <span key={index} className="recognize-agent-tag">
                  <span>{item}</span>
                  <span
                    className="recognize-agent-tag__close"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose(index);
                      const newTags = tags.filter((_, i) => i !== index);
                      setTags(newTags);
                      handleQueryChange(newTags);
                    }}
                  >×</span>
                </span>
              ))
            }
            onChange={(newTags, cur) => {
              if (cur?.e?.keyCode === 8 || cur?.e?.keyCode === 46) {
                const updatedTags = newTags || [];
                setTags(updatedTags);
                handleQueryChange(updatedTags);
                return;
              }
              if (!newTags.length) return;
              const _curItems = splitByCommas(cur.item);
              const allTags = [...tags, ..._curItems].slice(0, 5);
              setTags(allTags);
              handleQueryChange(allTags);
            }}
            onClear={() => {
              setTags([]);
              handleQueryChange([]);
            }}
            clearable
          />
        </div>
      );
    }

    if (optionValue === 3) {
      return (
        <div className="recognize-agent-inline-params">
          <div className="recognize-agent-dynamic-field__label">总结主题</div>
          <Textarea
            placeholder="请输入总结主题，如：工作经历"
            value={inputValue}
            maxlength={100}
            autosize={{ minRows: 4, maxRows: 8 }}
            onChange={(val) => {
              // 总结提炼：Query 限制最大 100 字符，超出自动截断
              const safeVal = typeof val === 'string' ? val.slice(0, 100) : val;
              setInputValue(safeVal);
              handleQueryChange(safeVal ? [safeVal] : []);
            }}
          />
        </div>
      );
    }

    if (optionValue === 4) {
      const schemaItems = (allParamsValue?.SchemaItems || []).map((s) => ({
        KeyName: s.KeyName || '',
        KeyType: s.KeyType || 0,
        KeyPrompt: s.KeyPrompt || '',
        SubItems: (s.SubItems || []).map((l2) => ({
          KeyName: l2.KeyName || '',
          KeyType: l2.KeyType || 0,
          KeyPrompt: l2.KeyPrompt || '',
          SubItems: (l2.SubItems || []).map((l3) => ({
            KeyName: l3.KeyName || '',
            KeyType: l3.KeyType || 0,
            KeyPrompt: l3.KeyPrompt || '',
          })),
        })),
      }));

      return (
        <div className="recognize-agent-inline-params">
          <div className="recognize-agent-dynamic-field__label">
            <span>自定义提取字段</span>
            <Tooltip content="支持配置一级字段及多层级子字段，点击齿轮配置子字段" theme="dark">
              <span className="ocrDemo-icon-question"></span>
            </Tooltip>
          </div>
          <FieldSchemaEditor
            value={schemaItems}
            enableCoord={enableCoord}
            onCoordChange={handleCoordChange}
            onChange={(newFields) => {
              // 保存完整结构到 paramsValue（用于 UI 回显）
              onParamsChange && onParamsChange({
                SchemaItems: newFields,
                Query: [],
              });
            }}
          />
        </div>
      );
    }

    return null;
  };

  // 计算参数区插入位置：紧跟选中卡片所在行的末尾（保证同行卡片保持原位）
  const COLUMNS = 2;
  const options = params.options || [];
  const selectedIndex = options.findIndex((opt) => opt.value === value);
  // 该行最后一个卡片索引；若未选中则为 -1
  const insertAfterIndex =
    selectedIndex >= 0
      ? Math.min(
          selectedIndex + (COLUMNS - 1 - (selectedIndex % COLUMNS)),
          options.length - 1
        )
      : -1;

  return (
    <>
      <Form.Item className="column" label={params.label}>
        <div className="recognize-agent-task-type-list">
          {options.map((option, index) => (
            <React.Fragment key={option.value}>
              <div
                className={`recognize-agent-task-type-item ${value === option.value ? 'active' : ''}`}
                onClick={() => onChange && onChange(option.value, option.value, option)}
              >
                <span className="recognize-agent-task-type-item__title">{option.text}</span>
                <span className="recognize-agent-task-type-item__des">{option.des}</span>
              </div>
              {index === insertAfterIndex && renderInlineParams(value) && (
                <div className="recognize-agent-inline-params-wrapper">
                  {renderInlineParams(value)}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </Form.Item>
    </>
  );
}

export {
  RecognizeAgentTaskTypeRenderer
};
